import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import AppShell from "../components/layout/AppShell";
import { Parse } from "../lib/parse";
import Icon from "../components/ui/Icon";

const DASHBOARD_PENDING_LIMIT = 12;
const DASHBOARD_PENDING_FETCH_LIMIT = 1000;

const localeByLanguage = {
  es: "es-MX",
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT"
};
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDocDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === "object" && value.iso) {
    const parsed = new Date(value.iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const getDocValue = (doc, key) => {
  if (!doc) {
    return undefined;
  }
  if (typeof doc.get === "function") {
    return doc.get(key);
  }
  return doc?.[key];
};

const getDocId = (doc) => String(doc?.id || doc?.objectId || "").trim();
const normalizeEmailAddress = (value) => String(value || "").trim().toLowerCase();
const normalizeBooleanFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

const getSigners = (doc) => {
  const signers = getDocValue(doc, "Signers");
  return Array.isArray(signers) ? signers : [];
};

const hasSigners = (doc) => getSigners(doc).length > 0;

const getSignerId = (signer) =>
  signer?.objectId || signer?.UserId?.objectId || signer?.userId?.objectId || signer?.Id || null;
const getSignerEmail = (signer) =>
  normalizeEmailAddress(
    signer?.Email || signer?.email || signer?.UserId?.Email || signer?.UserPtr?.Email || signer?.User?.Email || ""
  );
const getAuditSignerId = (entry) =>
  entry?.UserPtr?.UserId?.objectId ||
  entry?.UserPtr?.objectId ||
  entry?.SignerPtr?.objectId ||
  entry?.ContactPtr?.objectId ||
  null;
const getAuditSignerEmail = (entry) =>
  normalizeEmailAddress(
    entry?.UserPtr?.Email || entry?.Email || entry?.email || entry?.SignerPtr?.Email || entry?.ContactPtr?.Email || ""
  );

const getSignerEntryId = (entry) =>
  entry?.signerObjId ||
  entry?.signerPtr?.objectId ||
  entry?.UserPtr?.objectId ||
  entry?.UserId?.objectId ||
  entry?.objectId ||
  null;
const getSignerEntryEmail = (entry) =>
  normalizeEmailAddress(
    entry?.signerPtr?.Email || entry?.Email || entry?.email || entry?.UserId?.Email || entry?.UserPtr?.Email || ""
  );

const isSameSignerIdentity = (left, right) => {
  const leftId = String(getSignerEntryId(left) || "").trim();
  const rightId = String(getSignerEntryId(right) || "").trim();
  if (leftId && rightId && leftId === rightId) {
    return true;
  }
  const leftEmail = getSignerEntryEmail(left);
  const rightEmail = getSignerEntryEmail(right);
  return Boolean(leftEmail && rightEmail && leftEmail === rightEmail);
};

const getPlaceholderWidgets = (placeholder) => {
  if (!placeholder || typeof placeholder !== "object") {
    return [];
  }
  if (Array.isArray(placeholder?.pos)) {
    return placeholder.pos;
  }
  if (Array.isArray(placeholder?.placeHolder)) {
    return placeholder.placeHolder;
  }
  if (Array.isArray(placeholder?.placeholders)) {
    return placeholder.placeholders;
  }
  return [];
};

const hasSignatureWidgetResponse = (widget) => {
  if (!widget || typeof widget !== "object") {
    return false;
  }
  const widgetType = String(widget?.type || "").trim().toLowerCase();
  if (!["signature", "initials", "stamp", "image"].includes(widgetType)) {
    return false;
  }
  const response =
    widget?.options?.response ??
    widget?.response ??
    widget?.value ??
    widget?.options?.value ??
    null;
  if (typeof response === "string") {
    return Boolean(response.trim());
  }
  if (Array.isArray(response)) {
    return response.length > 0;
  }
  return Boolean(response);
};

const hasSignerSigned = (signer, auditTrail = []) => {
  const signerId = String(getSignerId(signer) || "").trim();
  const signerEmail = getSignerEmail(signer);
  return auditTrail.some((entry) => {
    if (String(entry?.Activity || "").toLowerCase() !== "signed") {
      return false;
    }
    const auditSignerId = String(getAuditSignerId(entry) || "").trim();
    const auditSignerEmail = getAuditSignerEmail(entry);
    if (signerId && auditSignerId && signerId === auditSignerId) {
      return true;
    }
    if (signerEmail && auditSignerEmail && signerEmail === auditSignerEmail) {
      return true;
    }
    return false;
  });
};

const isSignerMarkedSigned = (signer) => {
  if (!signer || typeof signer !== "object") {
    return false;
  }
  const normalizedStatus = String(
    signer?.Status || signer?.status || signer?.Activity || signer?.activity || ""
  )
    .trim()
    .toUpperCase();
  if (["SIGNED", "COMPLETED", "DONE"].includes(normalizedStatus)) {
    return true;
  }
  return (
    normalizeBooleanFlag(signer?.IsSigned) ||
    normalizeBooleanFlag(signer?.isSigned) ||
    Boolean(signer?.SignedOn || signer?.signedOn || signer?.signedAt)
  );
};

const hasSignerSignedWidgets = (doc, signer) => {
  const placeholders = getDocValue(doc, "Placeholders");
  const normalized = Array.isArray(placeholders) ? placeholders : [];
  return normalized.some((placeholder) => {
    if (!isSameSignerIdentity(signer, placeholder)) {
      return false;
    }
    return getPlaceholderWidgets(placeholder).some((widget) => hasSignatureWidgetResponse(widget));
  });
};

const hasSignerCompletedSigning = (doc, signer, auditTrail = []) =>
  hasSignerSigned(signer, auditTrail) || isSignerMarkedSigned(signer);

const findCurrentSigner = (doc, currentUserId = "", currentUserEmail = "") => {
  const signers = getSigners(doc);
  const fromSigners =
    signers.find((signer) => {
      const signerId = String(getSignerId(signer) || "").trim();
      const signerEmail = getSignerEmail(signer);
      return (
        (currentUserId && signerId && signerId === currentUserId) ||
        (currentUserEmail && signerEmail && signerEmail === currentUserEmail)
      );
    }) || null;
  if (fromSigners) {
    return fromSigners;
  }
  const placeholders = getDocValue(doc, "Placeholders");
  const normalized = Array.isArray(placeholders) ? placeholders : [];
  return (
    normalized.find((placeholder) => {
      const signerId = String(getSignerEntryId(placeholder) || "").trim();
      const signerEmail = getSignerEntryEmail(placeholder);
      return (
        (currentUserId && signerId && signerId === currentUserId) ||
        (currentUserEmail && signerEmail && signerEmail === currentUserEmail)
      );
    }) || null
  );
};

const getNeedYourSignAccessState = (doc, currentUserId = "", currentUserEmail = "") => {
  const signers = getSigners(doc);
  const currentSigner = findCurrentSigner(doc, currentUserId, currentUserEmail);
  if (!currentSigner) {
    return {
      isSigner: false,
      alreadySigned: false,
      waitingTurn: false,
      canSign: false
    };
  }
  const auditTrail = Array.isArray(getDocValue(doc, "AuditTrail")) ? getDocValue(doc, "AuditTrail") : [];
  if (hasSignerCompletedSigning(doc, currentSigner, auditTrail)) {
    return {
      isSigner: true,
      alreadySigned: true,
      waitingTurn: false,
      canSign: false
    };
  }
  const nextPendingSigner =
    (signers.length
      ? signers.find((signer) => !hasSignerCompletedSigning(doc, signer, auditTrail))
      : currentSigner) || null;
  if (!nextPendingSigner) {
    return {
      isSigner: true,
      alreadySigned: true,
      waitingTurn: false,
      canSign: false
    };
  }
  const currentSignerId = String(getSignerId(currentSigner) || "").trim();
  const currentSignerEmail = getSignerEmail(currentSigner);
  const nextSignerId = String(getSignerId(nextPendingSigner) || "").trim();
  const nextSignerEmail = getSignerEmail(nextPendingSigner);
  const isNextSigner =
    (currentSignerId && nextSignerId && currentSignerId === nextSignerId) ||
    (currentSignerEmail && nextSignerEmail && currentSignerEmail === nextSignerEmail);
  return {
    isSigner: true,
    alreadySigned: false,
    waitingTurn: !isNextSigner,
    canSign: isNextSigner
  };
};

const canCurrentUserOpenSigning = (doc, currentUserId = "", currentUserEmail = "") =>
  getNeedYourSignAccessState(doc, currentUserId, currentUserEmail).canSign;

const getSignedSignerIds = (doc) => {
  const auditTrail = getDocValue(doc, "AuditTrail");
  if (!Array.isArray(auditTrail)) {
    return new Set();
  }

  const signerIds = auditTrail
    .filter((entry) => String(entry?.Activity || "").toLowerCase() === "signed")
    .map(
      (entry) =>
        entry?.UserPtr?.objectId ||
        entry?.SignerPtr?.objectId ||
        entry?.ContactPtr?.objectId ||
        null
    )
    .filter(Boolean);

  return new Set(signerIds);
};

const getDocTitle = (doc, index, t) =>
  getDocValue(doc, "Name") ||
  getDocValue(doc, "Title") ||
  getDocValue(doc, "FileName") ||
  t("new-ui.dashboard.default-document-title", {
    index: index + 1,
    defaultValue: `Document ${index + 1}`
  });

const truncateText = (value, maxLength = 44) => {
  if (typeof value !== "string") {
    return "";
  }
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength).trimEnd()}...`;
};

const getSignerName = (signer, index, t) =>
  signer?.Name ||
  signer?.name ||
  signer?.Email ||
  signer?.email ||
  t("new-ui.dashboard.default-signer", { index: index + 1, defaultValue: `Signer ${index + 1}` });

const getPendingSignerNames = (doc, t) => {
  const signers = getSigners(doc);
  if (!signers.length) {
    return [];
  }

  const signedIds = getSignedSignerIds(doc);
  const pendingById = signers.filter((signer) => {
    const signerId = getSignerId(signer);
    if (!signerId) {
      return true;
    }
    return !signedIds.has(signerId);
  });

  return pendingById.map((signer, index) => getSignerName(signer, index, t));
};

const getNextSignerName = (doc, t) => {
  const signers = getSigners(doc);
  if (!signers.length) {
    return t("new-ui.dashboard.no-data", { defaultValue: "No data" });
  }

  const signedIds = getSignedSignerIds(doc);
  const nextSigner = signers.find((signer) => {
    const signerId = getSignerId(signer);
    if (!signerId) {
      return true;
    }
    return !signedIds.has(signerId);
  });

  if (!nextSigner) {
    return t("new-ui.dashboard.no-data", { defaultValue: "No data" });
  }

  return getSignerName(nextSigner, 0, t);
};

const getAllSignerNames = (doc, t) =>
  getSigners(doc).map((signer, index) => getSignerName(signer, index, t));

const isExpiringSoon = (date, days = 2) => {
  const expiryDate = parseDocDate(date);
  if (!expiryDate) {
    return false;
  }
  const diff = expiryDate.getTime() - Date.now();
  return diff >= 0 && diff <= days * MILLISECONDS_PER_DAY;
};

const getSentStatus = (doc, t) => {
  const expiryDate = parseDocDate(getDocValue(doc, "ExpiryDate"));
  if (expiryDate && expiryDate.getTime() < Date.now()) {
    return {
      label: t("sidebar.Documents-Children.Expired", { defaultValue: "Expired" }),
      tone: "danger"
    };
  }
  if (getDocValue(doc, "IsCompleted")) {
    return {
      label: t("sidebar.Documents-Children.Completed", { defaultValue: "Completed" }),
      tone: "success"
    };
  }
  if (getDocValue(doc, "IsDeclined")) {
    return {
      label: t("sidebar.Documents-Children.Declined", { defaultValue: "Declined" }),
      tone: "danger"
    };
  }
  if (!hasSigners(doc)) {
    return {
      label: t("new-ui.dashboard.status.pending-setup", { defaultValue: "Pending setup" }),
      tone: "warning"
    };
  }
  return {
    label: t("new-ui.dashboard.status.sent", { defaultValue: "Sent" }),
    tone: "info"
  };
};

const normalizeCloudResult = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.result)) {
    return payload.result;
  }
  return [];
};

const getParseFunctionBaseUrl = () => {
  const baseUrl = String(localStorage.getItem("baseUrl") || "").trim();
  if (!baseUrl) {
    return "";
  }
  if (baseUrl.startsWith(":")) {
    return `${window.location.protocol}//${window.location.hostname}${baseUrl}`;
  }
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
};

const getParseFunctionHeaders = () => {
  const parseAppId = String(localStorage.getItem("parseAppId") || "").trim();
  const sessionToken = String(localStorage.getItem("accesstoken") || "").trim();
  return {
    "Content-Type": "application/json",
    ...(parseAppId ? { "X-Parse-Application-Id": parseAppId } : {}),
    ...(sessionToken ? { sessiontoken: sessionToken } : {}),
    ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {})
  };
};

const fetchReportRows = async (reportId, { skip = 0, limit = 100 } = {}) => {
  const payload = { reportId, skip, limit };
  const baseUrl = getParseFunctionBaseUrl();

  if (baseUrl) {
    try {
      const response = await axios.post(`${baseUrl}functions/getReport`, payload, {
        headers: getParseFunctionHeaders()
      });
      const data = response?.data || {};
      const result = Object.prototype.hasOwnProperty.call(data, "result") ? data.result : data;
      const serverError = String(result?.error || data?.error || "").trim();
      if (serverError) {
        throw new Error(serverError);
      }
      return normalizeCloudResult(result);
    } catch (requestError) {
      // Fallback to Parse SDK call below.
    }
  }

  const cloudResponse = await Parse.Cloud.run("getReport", payload);
  const cloudError = String(cloudResponse?.error || cloudResponse?.result?.error || "").trim();
  if (cloudError) {
    throw new Error(cloudError);
  }
  return normalizeCloudResult(cloudResponse);
};

const hasInProgressRecipients = (record) => {
  const placeholders = Array.isArray(record?.Placeholders) ? record.Placeholders : [];
  const signers = Array.isArray(record?.Signers) ? record.Signers : [];
  return placeholders.length > 0 || signers.length > 0;
};

const isInProgressRecord = (record) => {
  if (!record || record?.Type === "Folder") {
    return false;
  }
  if (record?.IsArchive || record?.IsCompleted || record?.IsDeclined) {
    return false;
  }
  if (record?.IsDraft || record?.Draft) {
    return false;
  }
  const expiry = parseDocDate(record?.ExpiryDate);
  if (expiry && expiry.getTime() <= Date.now()) {
    return false;
  }
  return hasInProgressRecipients(record);
};

const isNeedYourSignRecordForCurrentUser = (record, currentUserId, currentUserEmail = "") => {
  if (!currentUserId && !currentUserEmail) {
    return true;
  }
  const accessState = getNeedYourSignAccessState(record, currentUserId, currentUserEmail);
  return accessState.isSigner && !accessState.alreadySigned;
};

function DashboardPage() {
  const { t, i18n } = useTranslation();
  const role = useMemo(() => {
    try {
      const extClass = JSON.parse(localStorage.getItem("Extand_Class") || "[]");
      return Array.isArray(extClass) ? String(extClass?.[0]?.UserRole || "").trim() : "";
    } catch (error) {
      return "";
    }
  }, []);
  const canManageTemplates = role !== "contracts_User";
  const sessionUser = Parse.User.current();
  const currentUserId = String(sessionUser?.id || sessionUser?.objectId || "").trim();
  const currentUserEmail = normalizeEmailAddress(
    sessionUser?.get?.("email") ||
      sessionUser?.get?.("Email") ||
      sessionUser?.attributes?.email ||
      sessionUser?.attributes?.Email ||
      sessionUser?.email ||
      localStorage.getItem("userEmail") ||
      ""
  );
  const quickActions = useMemo(() => {
    const baseActions = [
      {
        label: t("new-ui.dashboard.quick-actions.request-signatures", { defaultValue: "Request signatures" }),
        icon: "mail",
        iconTone: "request",
        to: "/form/8mZzFxbG1z"
      },
      {
        label: t("new-ui.dashboard.quick-actions.sign-yourself", { defaultValue: "Sign yourself" }),
        icon: "signature",
        iconTone: "sign",
        to: "/form/sHAnZphf69"
      }
    ];
    if (canManageTemplates) {
      baseActions.push({
        label: t("new-ui.dashboard.quick-actions.create-template", { defaultValue: "Create template" }),
        icon: "templateDoc",
        iconTone: "template",
        to: "/form/template"
      });
    }
    return baseActions;
  }, [canManageTemplates, t]);

  const formatDate = (date) => {
    const normalizedDate = parseDocDate(date);
    if (!normalizedDate) {
      return "--";
    }
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || "es").split("-")[0];
    const locale = localeByLanguage[currentLanguage] || localeByLanguage.es;
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(normalizedDate);
  };

  const formatDateOnly = (date) => {
    const normalizedDate = parseDocDate(date);
    if (!normalizedDate) {
      return "--";
    }
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || "es").split("-")[0];
    const locale = localeByLanguage[currentLanguage] || localeByLanguage.es;
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }).format(normalizedDate);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingDocs, setPendingDocs] = useState([]);
  const [pendingDocsTotal, setPendingDocsTotal] = useState(0);
  const [recentSentDocs, setRecentSentDocs] = useState([]);
  const [recentSentDocsTotal, setRecentSentDocsTotal] = useState(0);
  const [recentSentSearch, setRecentSentSearch] = useState("");

  const filteredRecentSentDocs = useMemo(() => {
    const query = recentSentSearch.trim().toLowerCase();
    if (!query) {
      return recentSentDocs;
    }

    return recentSentDocs.filter((doc, index) => {
      const docTitle = String(getDocTitle(doc, index, t)).toLowerCase();
      const statusLabel = String(getSentStatus(doc, t).label || "").toLowerCase();
      const allSignersText = getAllSignerNames(doc, t).join(" ").toLowerCase();
      const pendingSignersText = getPendingSignerNames(doc, t).join(" ").toLowerCase();
      const docId = getDocId(doc).toLowerCase();

      return (
        docTitle.includes(query) ||
        statusLabel.includes(query) ||
        allSignersText.includes(query) ||
        pendingSignersText.includes(query) ||
        docId.includes(query)
      );
    });
  }, [recentSentDocs, recentSentSearch, t]);

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setLoading(true);
      setError("");
      setPendingDocsTotal(0);
      setRecentSentDocsTotal(0);

      try {
        const currentUser = Parse.User.current();
        if (!currentUser) {
          throw new Error("No active Parse user in session");
        }

        const [pendingReportRows, inProgressReportRows] = await Promise.all([
          fetchReportRows("4Hhwbp482K", {
            skip: 0,
            limit: DASHBOARD_PENDING_FETCH_LIMIT
          })
            .then((response) =>
              normalizeCloudResult(response).filter((record) =>
                isNeedYourSignRecordForCurrentUser(
                  record,
                  currentUser.id,
                  normalizeEmailAddress(
                    currentUser?.get?.("email") ||
                      currentUser?.get?.("Email") ||
                      currentUser?.attributes?.email ||
                      currentUser?.attributes?.Email ||
                      currentUser?.email ||
                      localStorage.getItem("userEmail") ||
                      ""
                  )
                )
              )
            )
            .catch((reportError) => {
              console.error("Need-your-sign report load failed", reportError);
              return [];
            }),
          fetchReportRows("1MwEuxLEkF", {
            skip: 0,
            limit: DASHBOARD_PENDING_FETCH_LIMIT
          })
            .then((response) => normalizeCloudResult(response))
            .catch((reportError) => {
              console.error("In-progress report load failed", reportError);
              return [];
            })
        ]);

        const sortByUpdatedDesc = (a, b) => {
          const dateA = parseDocDate(a?.updatedAt || a?.createdAt)?.getTime() || 0;
          const dateB = parseDocDate(b?.updatedAt || b?.createdAt)?.getTime() || 0;
          return dateB - dateA;
        };

        const pendingNeedYourSignature = (pendingReportRows || [])
          .filter((record) => getDocId(record))
          .sort(sortByUpdatedDesc);

        const recentInProgressDocs = (inProgressReportRows || [])
          .filter((record) => getDocId(record))
          .sort(sortByUpdatedDesc);

        if (!cancelled) {
          setPendingDocsTotal(pendingNeedYourSignature.length);
          setRecentSentDocsTotal(recentInProgressDocs.length);
          setPendingDocs(pendingNeedYourSignature.slice(0, DASHBOARD_PENDING_LIMIT));
          setRecentSentDocs(recentInProgressDocs.slice(0, DASHBOARD_PENDING_LIMIT));
        }
      } catch (dashboardError) {
        console.error("Dashboard load failed", dashboardError);
        if (!cancelled) {
          setError(
            t("new-ui.dashboard.errors.load-failed", {
              defaultValue: "Could not load the dashboard from backend."
            })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <AppShell>
      <div className="dana-home-grid">
        <article className="dana-panel animate-fade-up">
          <div className="dana-panel-head">
            <h2>{t("new-ui.dashboard.quick-actions.title", { defaultValue: "Quick actions" })}</h2>
          </div>
          <div className="dana-quick-grid-one-row">
            {quickActions.map((action) => (
              <Link key={action.label} to={action.to} className="dana-quick-btn dana-quick-btn-link">
                <span className={`dana-quick-icon dana-quick-icon-${action.iconTone}`}>
                  <Icon name={action.icon} />
                </span>
                {action.label}
              </Link>
            ))}
          </div>
        </article>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <article className="dana-panel animate-fade-up [animation-delay:60ms] xl:order-2">
            <Link
              to="/report/4Hhwbp482K"
              className="mb-3 block rounded-xl border border-indigo-200 bg-indigo-100 px-4 py-3 text-indigo-700 no-underline transition hover:bg-indigo-200/70"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-indigo-200 text-indigo-700">
                    <Icon name="signature" className="dana-need-signature-glyph" />
                  </span>
                  <p className="m-0 text-sm font-semibold leading-tight">
                    {t("dashboard-card.Need your Signature", { defaultValue: "Need your Signature" })}
                  </p>
                </div>
                <span className="text-right leading-tight">
                  {loading ? (
                    <strong className="block text-sm font-semibold">{t("loading", { defaultValue: "Loading..." })}</strong>
                  ) : (
                    <>
                      <strong className="block text-xl font-bold">{pendingDocsTotal}</strong>
                      <small className="text-[10px] font-semibold uppercase tracking-[0.05em] opacity-80">
                        {t("new-ui.dashboard.summary.documents-label", { defaultValue: "Documents" })}
                      </small>
                    </>
                  )}
                </span>
              </div>
            </Link>
            <div className="dana-panel-head">
              <h2 className="dana-title-with-icon">
                <span className="dana-title-icon" aria-hidden="true">
                  <Icon name="signature" />
                </span>
                {t("new-ui.dashboard.pending-documents.title", {
                  defaultValue: "Pending documents to sign"
                })}
              </h2>
            </div>
            {loading ? (
              <p className="dana-muted">{t("new-ui.dashboard.loading-documents", { defaultValue: "Loading documents..." })}</p>
            ) : null}
            {error ? <p className="text-app-danger">{error}</p> : null}

            {!loading && !error ? (
              <div>
                {pendingDocs.length ? (
                  <div className="overflow-x-hidden rounded-xl border border-app-line bg-white shadow-soft">
                    <table className="w-full table-fixed border-collapse text-left">
                      <thead className="bg-app-sand">
                        <tr>
                          <th className="w-[32%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Title", { defaultValue: "Document" })}
                          </th>
                          <th className="w-[16%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.expires", { defaultValue: "Expires" })}
                          </th>
                          <th className="w-[16%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.pending", { defaultValue: "Pending" })}
                          </th>
                          <th className="w-[24%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.next-signer", { defaultValue: "Next signer" })}
                          </th>
                          <th className="w-[12%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("btnLabel.Sign", { defaultValue: "Sign" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingDocs.map((doc, index) => {
                          const docId = getDocId(doc);
                          const docTitle = getDocTitle(doc, index, t);
                          const pendingSignerNames = getPendingSignerNames(doc, t);
                          const expiryDate = parseDocDate(getDocValue(doc, "ExpiryDate"));
                          const expiresSoon = isExpiringSoon(expiryDate);
                          const nextSigner = getNextSignerName(doc, t);
                          const accessState = getNeedYourSignAccessState(doc, currentUserId, currentUserEmail);
                          const canOpenSigning = accessState.canSign;
                          const blockedSignTitle = accessState.alreadySigned
                            ? t("already-signed-message", {
                                defaultValue: "You have already signed this document."
                              })
                            : t("new-ui.history.need-your-sign.waiting-turn-short", {
                                defaultValue: "Esperando firma anterior"
                              });

                          return (
                            <tr key={docId || `pending-${index}`} className="border-t border-app-line align-top">
                              <td className="px-2 py-1.5 text-[12px] font-medium text-app-ink leading-tight">
                                {canOpenSigning ? (
                                  <Link
                                    to={`/internalSign/${docId}`}
                                    className="block truncate text-indigo-600 hover:text-indigo-700 hover:underline"
                                    title={docTitle}
                                  >
                                    {truncateText(docTitle, 42)}
                                  </Link>
                                ) : (
                                  <span className="block truncate text-app-ink-muted" title={`${docTitle} - ${blockedSignTitle}`}>
                                    {truncateText(docTitle, 42)}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                <span
                                  className={`inline-flex items-center gap-1 leading-tight ${
                                    expiresSoon ? "font-semibold text-amber-600" : "text-app-ink-muted"
                                  }`}
                                  title={
                                    expiresSoon
                                      ? t("new-ui.dashboard.rows.expiring-soon", {
                                          defaultValue: "Expires in 2 days or less"
                                        })
                                      : undefined
                                  }
                                >
                                  {expiresSoon ? <Icon name="alertTriangle" className="h-3.5 w-3.5" /> : null}
                                  {formatDateOnly(expiryDate)}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                {t("new-ui.dashboard.rows.signatures-count", {
                                  count: pendingSignerNames.length,
                                  defaultValue: `${pendingSignerNames.length} signatures`
                                })}
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted leading-tight">
                                <span className="block break-words" title={nextSigner}>
                                  {nextSigner}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                {canOpenSigning ? (
                                  <Link
                                    to={`/internalSign/${docId}`}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-indigo-200 bg-indigo-50 text-indigo-600 transition hover:bg-indigo-100 hover:text-indigo-700"
                                    title={t("btnLabel.Sign", { defaultValue: "Sign" })}
                                    aria-label={t("btnLabel.Sign", { defaultValue: "Sign" })}
                                  >
                                    <Icon name="edit" className="h-2.5 w-2.5" />
                                  </Link>
                                ) : (
                                  <span
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-slate-400"
                                    title={blockedSignTitle}
                                    aria-label={blockedSignTitle}
                                  >
                                    <Icon name="clock" className="h-3.5 w-3.5" />
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="dana-muted">
                    {t("new-ui.dashboard.pending-documents.empty", {
                      defaultValue: "You have no pending documents to sign."
                    })}
                  </p>
                )}
              </div>
            ) : null}
            <div className="mt-3 flex justify-end">
              <Link
                to="/report/4Hhwbp482K"
                className="inline-flex items-center rounded-lg border border-app-line bg-white px-3 py-1.5 text-xs font-semibold text-indigo-600 no-underline transition hover:border-indigo-300 hover:bg-indigo-50"
              >
                {t("new-ui.dashboard.actions.view-all", { defaultValue: "Ver todos" })}
              </Link>
            </div>
          </article>

          <article className="dana-panel animate-fade-up [animation-delay:90ms] xl:order-1">
            <Link
              to="/report/1MwEuxLEkF"
              className="mb-3 block rounded-xl border border-orange-200 bg-orange-100 px-4 py-3 text-orange-700 no-underline transition hover:bg-orange-200/70"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-orange-200 text-orange-700">
                    <Icon name="timeline" className="dana-out-signatures-glyph" />
                  </span>
                  <p className="m-0 text-sm font-semibold leading-tight">
                    {t("dashboard-card.Out for signatures", { defaultValue: "Out for signatures" })}
                  </p>
                </div>
                <span className="text-right leading-tight">
                  {loading ? (
                    <strong className="block text-sm font-semibold">{t("loading", { defaultValue: "Loading..." })}</strong>
                  ) : (
                    <>
                      <strong className="block text-xl font-bold">{recentSentDocsTotal}</strong>
                      <small className="text-[10px] font-semibold uppercase tracking-[0.05em] opacity-80">
                        {t("new-ui.dashboard.summary.documents-label", { defaultValue: "Documents" })}
                      </small>
                    </>
                  )}
                </span>
              </div>
            </Link>
            <div className="dana-panel-head">
              <h2 className="dana-title-with-icon">
                <span className="dana-title-icon dana-title-icon-request" aria-hidden="true">
                  <Icon name="mail" />
                </span>
                {t("new-ui.dashboard.recent-sent-documents.title", {
                  defaultValue: "Latest sent for signature"
                })}
              </h2>
              {!loading && !error && recentSentDocs.length ? (
                <input
                  type="search"
                  value={recentSentSearch}
                  onChange={(event) => setRecentSentSearch(event.target.value)}
                  placeholder={t("new-ui.dashboard.recent-sent-documents.search-placeholder", {
                    defaultValue: "Search"
                  })}
                  className="w-[168px] sm:w-[220px] rounded-lg border border-app-line bg-white px-2.5 py-1 text-[11px] text-app-ink outline-none transition focus:border-app-brand focus:ring-2 focus:ring-app-brand/20"
                />
              ) : null}
            </div>
            {loading ? (
              <p className="dana-muted">{t("new-ui.dashboard.loading-documents", { defaultValue: "Loading documents..." })}</p>
            ) : null}
            {error ? <p className="text-app-danger">{error}</p> : null}

            {!loading && !error ? (
              <div>
                {recentSentDocs.length ? (
                  <div className="space-y-2">
                  <div className="overflow-x-hidden rounded-xl border border-app-line bg-white shadow-soft">
                    <table className="w-full table-fixed border-collapse text-left">
                      <thead className="bg-app-sand">
                        <tr>
                          <th className="w-[38%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("report-heading.Title", { defaultValue: "Document" })}
                          </th>
                          <th className="w-[20%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.last-activity", { defaultValue: "Last activity" })}
                          </th>
                          <th className="w-[18%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.pending", { defaultValue: "Pending" })}
                          </th>
                          <th className="w-[24%] px-2 py-1.5 text-[11px] font-semibold text-app-ink-muted">
                            {t("new-ui.dashboard.rows.next-signer", { defaultValue: "Next signer" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRecentSentDocs.map((doc, index) => {
                          const docId = getDocId(doc);
                          const docTitle = getDocTitle(doc, index, t);
                          const pendingSignerNames = getPendingSignerNames(doc, t);
                          const nextSigner = getNextSignerName(doc, t);

                          return (
                            <tr key={docId || `in-progress-${index}`} className="border-t border-app-line align-top">
                              <td className="px-2 py-1.5 text-[12px] font-medium text-app-ink leading-tight">
                                <span
                                  className="block truncate text-app-ink"
                                  title={docTitle}
                                >
                                  {truncateText(docTitle, 42)}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                {formatDateOnly(doc.updatedAt)}
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted">
                                {t("new-ui.dashboard.rows.signatures-count", {
                                  count: pendingSignerNames.length,
                                  defaultValue: `${pendingSignerNames.length} signatures`
                                })}
                              </td>
                              <td className="px-2 py-1.5 text-[11px] text-app-ink-muted leading-tight">
                                <span className="block break-words" title={nextSigner}>
                                  {nextSigner}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </div>
                ) : (
                  <p className="dana-muted">
                    {t("new-ui.dashboard.recent-sent-documents.empty", {
                      defaultValue: "There are no recently sent documents for signature."
                    })}
                  </p>
                )}
                {recentSentDocs.length && !filteredRecentSentDocs.length ? (
                  <p className="dana-muted">
                    {t("new-ui.dashboard.recent-sent-documents.no-results", {
                      defaultValue: "No hay resultados para tu busqueda."
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="mt-3 flex justify-end">
              <Link
                to="/report/1MwEuxLEkF"
                className="inline-flex items-center rounded-lg border border-app-line bg-white px-3 py-1.5 text-xs font-semibold text-indigo-600 no-underline transition hover:border-indigo-300 hover:bg-indigo-50"
              >
                {t("new-ui.dashboard.actions.view-all", { defaultValue: "Ver todos" })}
              </Link>
            </div>
          </article>
        </div>
      </div>
    </AppShell>
  );
}

export default DashboardPage;
